import { Navigate, Outlet, useLocation } from 'react-router';
import { useSuperadminAuth } from '@/contexts/superadmin-auth-context';
import { SuperadminSideNav } from './superadmin-side-nav';

interface SuperadminLayoutProps {
  children?: React.ReactNode;
}

interface SuperadminAccessRedirectOptions {
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  path: string;
}

export function getSuperadminAccessRedirect({
  isAuthenticated,
  isSuperAdmin,
  path,
}: SuperadminAccessRedirectOptions): string | null {
  if (!isAuthenticated) {
    return `/superadmin/login?redirect=${encodeURIComponent(path)}`;
  }

  if (!isSuperAdmin) {
    return '/dashboard';
  }

  return null;
}

export function SuperadminLayout({ children }: SuperadminLayoutProps) {
  const location = useLocation();
  const { isAuthenticated, isReady, user } = useSuperadminAuth();

  if (!isReady) {
    return <div data-testid="superadmin-layout-loading" className="min-h-full bg-background" />;
  }

  const redirectTo = getSuperadminAccessRedirect({
    isAuthenticated,
    isSuperAdmin: user?.isSuperAdmin === true,
    path: `${location.pathname}${location.search}`,
  });

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div data-testid="superadmin-layout" className="min-h-full bg-background">
      <div className="grid min-h-full gap-6 p-6 lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="rounded-xl border border-border bg-card p-3">
          <SuperadminSideNav />
        </aside>
        <main className="min-w-0 rounded-xl border border-border bg-card p-6">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}